import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { applyPlatformLanguage, storedPlatformLanguage } from '@/lib/i18n';
import { instituteAdminApi } from '@/lib/api';

// Layouts
import AuthLayout from '@/layouts/AuthLayout';
import DashboardLayout from '@/layouts/DashboardLayout';
import TestLayout from '@/layouts/TestLayout';

// Auth Pages — kept EAGER: these are the first thing a cold start renders, so
// splitting them out would only add a network/disk round-trip before login.
import LoginPage from '@/pages/auth/LoginPage';
import LabLogin from '@/pages/auth/LabLogin';

// Suspense fallback for every lazily-loaded page below (dependency-free).
import RouteFallback from '@/components/ui/RouteFallback';

const LabLogins = lazy(() => import('@/pages/admin/LabLogins'));

// Admin Pages
const AdminDashboard = lazy(() => import('@/pages/admin/Dashboard'));
const InstitutesPage = lazy(() => import('@/pages/admin/Institutes'));
const AccessPermissions = lazy(() => import('@/pages/admin/AccessPermissions'));
const AddInstitutePage = lazy(() => import('@/pages/admin/AddInstitute'));
const InstituteDetailPage = lazy(() => import('@/pages/admin/InstituteDetail'));
const StudentsPage = lazy(() => import('@/pages/admin/Students'));
const UsersPage = lazy(() => import('@/pages/admin/Users'));
const AcademicsQuestionsPage = lazy(() => import('@/pages/admin/AcademicsQuestions'));
// Publishing the guided-tutor course. Reached by a super admin (who owns the
// platform chapters) AND by an institute admin (who owns their own and can hide
// a platform one), so it sits OUTSIDE the super-admin-only block below.
const TutorCurriculumPage = lazy(() => import('@/pages/admin/TutorCurriculum'));
const TutorProgressPage = lazy(() => import('@/pages/admin/TutorProgress'));
const TestAssignmentsPage = lazy(() => import('@/pages/admin/TestAssignments'));
const AnalyticsPage = lazy(() => import('@/pages/admin/Analytics'));
const MasterDataPage = lazy(() => import('@/pages/admin/MasterData'));
const NoticesPage = lazy(() => import('@/pages/admin/Notices'));
const RevenuePage = lazy(() => import('@/pages/admin/Revenue'));
const TransactionsPage = lazy(() => import('@/pages/admin/Transactions'));
const ConfigPage = lazy(() => import('@/pages/admin/Config'));
const EbooksVideosPage = lazy(() => import('@/pages/admin/EbooksVideos'));
const InstituteContentLibrary = lazy(() => import('@/pages/admin/InstituteContentLibrary'));
const InstituteFinanceAnalytics = lazy(() => import('@/pages/admin/InstituteFinanceAnalytics'));
const InstituteDocumentTemplates = lazy(() => import('@/pages/admin/InstituteDocumentTemplates'));
const SettingsPage = lazy(() => import('@/pages/admin/Settings'));
const StudentsReportPage = lazy(() => import('@/pages/admin/StudentsReport'));
const InstructorsReportPage = lazy(() => import('@/pages/admin/InstructorsReport'));

// Institute Admin Module Pages
const InstituteAdminDashboard = lazy(() => import('@/pages/admin/InstituteAdminDashboard'));
const StudentRegistration = lazy(() => import('@/pages/admin/StudentRegistration'));
const Finance = lazy(() => import('@/pages/admin/Finance'));
const FeeCollection = lazy(() => import('@/pages/admin/FeeCollection'));
const InstallmentTracker = lazy(() => import('@/pages/admin/InstallmentTracker'));
const Sessions = lazy(() => import('@/pages/admin/Sessions'));
const AttendanceReport = lazy(() => import('@/pages/admin/AttendanceReport'));
const StaffManagement = lazy(() => import('@/pages/admin/StaffManagement'));
const StaffProfilePage = lazy(() => import('@/pages/admin/StaffProfile'));
const AdminStudentProfile = lazy(() => import('@/pages/admin/StudentProfile'));
const InstallmentDefaulters = lazy(() => import('@/pages/admin/InstallmentDefaulters'));
const RevenueReport = lazy(() => import('@/pages/admin/RevenueReport'));
const GeneralRegisterPage = lazy(() => import('@/pages/admin/GeneralRegister'));
const InvoiceList = lazy(() => import('@/pages/admin/InvoiceList'));
const MCQManagement = lazy(() => import('@/pages/admin/MCQManagement'));
const PaymentGateway = lazy(() => import('@/pages/admin/PaymentGateway'));
const PaymentTransactions = lazy(() => import('@/pages/admin/PaymentTransactions'));
const CourseFees = lazy(() => import('@/pages/admin/CourseFees'));
const PrincipalMasterData = lazy(() => import('@/pages/admin/PrincipalMasterData'));
const ExamRulesPage = lazy(() => import('@/pages/admin/ExamRules'));
const InstituteAnalyticsPage = lazy(() => import('@/pages/admin/InstituteAnalytics'));
const ApprovalsQueuePage = lazy(() => import('@/pages/admin/ApprovalsQueue'));
const PrincipalDashboard = lazy(() => import('@/pages/admin/PrincipalDashboard'));
const InstructorDashboard = lazy(() => import('@/pages/admin/InstructorDashboard'));
const InstructorStudents = lazy(() => import('@/pages/admin/InstructorStudents'));
const InstructorContentAuthoring = lazy(() => import('@/pages/admin/InstructorContentAuthoring'));
const ContentReviewQueue = lazy(() => import('@/pages/admin/ContentReviewQueue'));
const HRDashboard = lazy(() => import('@/pages/admin/HRDashboard'));
const ReceptionistDashboard = lazy(() => import('@/pages/admin/ReceptionistDashboard'));
const ClassRoom = lazy(() => import('@/pages/admin/ClassRoom'));
const SeniorInstructorDashboard = lazy(() => import('@/pages/admin/SeniorInstructorDashboard'));
const LabMonitoring = lazy(() => import('@/pages/admin/LabMonitoring'));
const StudentPerformanceMonitor = lazy(() => import('@/pages/admin/StudentPerformanceMonitor'));
const LabDevices = lazy(() => import('@/pages/admin/LabDevices'));
const BatchReschedule = lazy(() => import('@/pages/admin/BatchReschedule'));
const DeviceLicense = lazy(() => import('@/pages/admin/DeviceLicense'));
const ExeSyncMonitor = lazy(() => import('@/pages/admin/ExeSyncMonitor'));
const RemoteConsole = lazy(() => import('@/pages/admin/RemoteConsole'));
const LiveClass = lazy(() => import('@/pages/admin/LiveClass'));
const InstituteStudentRegister = lazy(() => import('@/pages/admin/InstituteStudentRegister'));
const FrontOfficeDashboard = lazy(() => import('@/pages/admin/FrontOfficeDashboard'));
const AccountantDashboard = lazy(() => import('@/pages/admin/AccountantDashboard'));
const LicensePayables = lazy(() => import('@/pages/admin/LicensePayables'));
const FinanceExports = lazy(() => import('@/pages/admin/FinanceExports'));
const CounselorDashboard = lazy(() => import('@/pages/admin/CounselorDashboard'));
const HealthMonitor = lazy(() => import('@/pages/admin/HealthMonitor'));
const VersionManagement = lazy(() => import('@/pages/admin/VersionManagement'));
const ErrorReports = lazy(() => import('@/pages/admin/ErrorReports'));
const SoftwareDownloads = lazy(() => import('@/pages/admin/SoftwareDownloads'));

// Student Pages
const StudentDashboard = lazy(() => import('@/pages/student/Dashboard'));
const StudentProfile = lazy(() => import('@/pages/student/Profile'));
const TestHistoryPage = lazy(() => import('@/pages/student/TestHistory'));
const TestResultPage = lazy(() => import('@/pages/student/TestResult'));
const KeyboardPractice = lazy(() => import('@/pages/student/KeyboardPractice'));
const TypingPractice = lazy(() => import('@/pages/student/TypingPractice'));
const MCQPractice = lazy(() => import('@/pages/student/MCQPractice'));
const VideoTutorials = lazy(() => import('@/pages/student/VideoTutorials'));
const VideoPlayer = lazy(() => import('@/pages/student/VideoPlayer'));

// Student Course Pages
const TheoryCourse = lazy(() => import('@/pages/student/course/TheoryCourse'));

// Student Typing Test Pages
const TypingTestPage = lazy(() => import('@/pages/student/typing-test/TypingTestPage'));
const TypingTestSessionPage = lazy(() => import('@/pages/student/typing-test/TypingTestSessionPage'));

// Student Theory Test Pages
const TheoryTestPage = lazy(() => import('@/pages/student/theory-test/TheoryTestPage'));

// Student Other Pages
const DemoExam = lazy(() => import('@/pages/student/DemoExam'));
const StudentResults = lazy(() => import('@/pages/student/results/Results'));

// Test Session
const TestSessionPage = lazy(() => import('@/pages/test/TestSession'));

// Protected Route Component
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import ServerLicenseGate from '@/components/ServerLicenseGate';

const institutePortalRoles = [
    'institute_admin',
    'principal',
    'instructor',
    'senior_instructor',
    'admin_staff',
    'accountant',
    'hr',
    'receptionist',
    'counselor',
    'operations',
] as const;

export default function App() {
    const { isAuthenticated, user, labSession } = useAuthStore();

    // Platform UI language — institute-selected; superadmin is always English.
    // Apply the last-known-for-this-institute value immediately (avoids a
    // flash of English), then reconcile with the server so every device for
    // this institute converges on the same language instead of drifting
    // per-browser.
    useEffect(() => {
        const instituteId = user?.institute_id || null;
        applyPlatformLanguage(storedPlatformLanguage(instituteId), user?.role, instituteId);

        if (!instituteId || user?.role === 'super_admin') return;
        instituteAdminApi.getPlatformLanguage()
            .then((data) => applyPlatformLanguage(data?.platform_language || 'english', user?.role, instituteId))
            .catch(() => { /* keep the locally-cached value on failure */ });
    }, [user?.role, user?.institute_id]);

    return (
        // Before anything else in the Institute EXE: which institute is this
        // server? Until it has an activation key it has no answer, and the
        // relay, the sync bundle and every lab client depend on that answer.
        // In a browser this renders its children unchanged.
        <ServerLicenseGate>
        <Suspense fallback={<RouteFallback />}>
            <Routes>
            {/* Auth Routes */}
            <Route element={<AuthLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/super-admin/login" element={<Navigate to="/login" replace />} />
            </Route>

            {/* Browser Lab Login — its own full-screen page, no AuthLayout chrome */}
            <Route path="/lab/login" element={<LabLogin />} />
            <Route path="/lab" element={<Navigate to="/lab/login" replace />} />

            {/* Admin Routes */}
            <Route
                element={
                    <ProtectedRoute allowedRoles={['super_admin', ...institutePortalRoles]}>
                        <DashboardLayout />
                    </ProtectedRoute>
                }
            >
                {/* Super-admin-only platform pages (institute roles are redirected home) */}
                <Route element={<ProtectedRoute allowedRoles={['super_admin']} />}>
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/admin/institutes" element={<InstitutesPage />} />
                    <Route path="/admin/institutes/add" element={<AddInstitutePage />} />
                    <Route path="/admin/institutes/:id/edit" element={<AddInstitutePage />} />
                    <Route path="/admin/institutes/:id" element={<InstituteDetailPage />} />
                    <Route path="/admin/users" element={<UsersPage />} />
                    <Route path="/admin/analytics" element={<AnalyticsPage />} />
                    <Route path="/admin/revenue" element={<RevenuePage />} />
                    <Route path="/admin/transactions" element={<TransactionsPage />} />
                    <Route path="/admin/config" element={<ConfigPage />} />
                    <Route path="/admin/students-report" element={<StudentsReportPage />} />
                    <Route path="/admin/instructors-report" element={<InstructorsReportPage />} />
                    <Route path="/admin/assignments" element={<TestAssignmentsPage />} />
                    <Route path="/admin/system-health" element={<HealthMonitor />} />
                    <Route path="/admin/version-management" element={<VersionManagement />} />
                    <Route path="/admin/remote-console" element={<RemoteConsole />} />
                    <Route path="/admin/error-reports" element={<ErrorReports />} />
                    <Route path="/admin/lab-logins" element={<LabLogins />} />
                    {/* Platform-level artwork and rule catalogs live above any one
                        institute, so they stay true super-admin-only pages. */}
                    <Route path="/admin/document-templates" element={<InstituteDocumentTemplates />} />
                    <Route path="/admin/exam-rules" element={<ExamRulesPage />} />
                    <Route path="/admin/access-permissions" element={<AccessPermissions />} />
                    <Route path="/admin/payment-gateway" element={<PaymentGateway />} />
                </Route>
                <Route path="/admin/students" element={<StudentsPage />} />
                <Route path="/admin/questions" element={<AcademicsQuestionsPage />} />
                <Route path="/admin/tutor-curriculum" element={<TutorCurriculumPage />} />
                <Route path="/admin/tutor-progress" element={<TutorProgressPage />} />
                <Route path="/admin/master-data" element={<MasterDataPage />} />
                <Route path="/admin/notices" element={<NoticesPage />} />
                <Route path="/admin/content-library" element={<InstituteContentLibrary />} />
                <Route path="/admin/settings" element={<SettingsPage />} />

                <Route path="/admin/institute-dashboard" element={<InstituteAdminDashboard />} />
                <Route path="/admin/principal-master-data" element={<PrincipalMasterData />} />
                <Route path="/admin/student-registration" element={<StudentRegistration />} />
                <Route path="/admin/finance" element={<Finance />} />
                <Route path="/admin/fee-collection" element={<FeeCollection />} />
                <Route path="/admin/installment-tracker" element={<InstallmentTracker />} />
                <Route path="/admin/sessions" element={<Sessions />} />
                <Route path="/admin/attendance-report" element={<AttendanceReport />} />
                <Route path="/admin/staff" element={<StaffManagement />} />
                <Route path="/admin/student-profile/:id" element={<AdminStudentProfile />} />
                <Route path="/admin/staff-profile/:id" element={<StaffProfilePage />} />
                <Route path="/admin/general-register" element={<GeneralRegisterPage />} />
                <Route path="/admin/installment-defaulters" element={<InstallmentDefaulters />} />
                <Route path="/admin/revenue-report" element={<RevenueReport />} />
                <Route path="/admin/finance-analytics" element={<InstituteFinanceAnalytics />} />
                <Route path="/admin/invoice-list" element={<InvoiceList />} />
                <Route path="/admin/mcq-management" element={<MCQManagement />} />
                <Route path="/admin/course-fees" element={<CourseFees />} />
                <Route path="/admin/payment-transactions" element={<PaymentTransactions />} />
                <Route path="/admin/batch-reschedule" element={<BatchReschedule />} />
                <Route path="/admin/student-register" element={<InstituteStudentRegister />} />
                <Route path="/admin/institute-analytics" element={<InstituteAnalyticsPage />} />
                <Route path="/admin/instructor-dashboard" element={<InstructorDashboard />} />
                <Route path="/admin/instructor-students" element={<InstructorStudents />} />
                <Route path="/admin/instructor-content" element={<InstructorContentAuthoring />} />
                <Route
                    path="/admin/content-review"
                    element={
                        <ProtectedRoute allowedRoles={['super_admin', 'institute_admin', 'principal']}>
                            <ContentReviewQueue />
                        </ProtectedRoute>
                    }
                />
                <Route path="/admin/hr-dashboard" element={<HRDashboard />} />
                <Route path="/admin/receptionist" element={<ReceptionistDashboard />} />
                <Route path="/admin/class-room" element={<ClassRoom />} />
                <Route path="/admin/senior-instructor-dashboard" element={<SeniorInstructorDashboard />} />
                <Route path="/admin/principal-dashboard" element={<PrincipalDashboard />} />
                <Route path="/admin/front-office" element={<FrontOfficeDashboard />} />
                <Route path="/admin/accountant-dashboard" element={<AccountantDashboard />} />
                <Route path="/admin/license-payables" element={<LicensePayables />} />
                <Route path="/admin/finance-exports" element={<FinanceExports />} />
                <Route path="/admin/counselor-dashboard" element={<CounselorDashboard />} />
                {/* Learning resources: BOTH sides need this one. The platform
                    publishes videos here, and an institute must be able to see the
                    ones published to it -- so it sits under the shared guard above
                    (super admin OR an institute role), not in either half.
                    Writes to platform rows are refused by the backend and the page
                    offers no buttons for them. */}
                <Route path="/admin/ebooks-videos" element={<EbooksVideosPage />} />
                <Route element={<ProtectedRoute allowedRoles={[...institutePortalRoles]} />}>
                    <Route path="/admin/lab-monitoring" element={<LabMonitoring />} />
                    <Route path="/admin/performance-monitor" element={<StudentPerformanceMonitor />} />
                    <Route path="/admin/lab-devices" element={<LabDevices />} />
                    <Route path="/admin/device-license" element={<DeviceLicense />} />
                    <Route path="/admin/device-licensing-overview" element={<Navigate to="/admin/device-license" replace />} />
                    <Route path="/admin/exe-sync" element={<ExeSyncMonitor />} />
                    <Route path="/admin/live-class" element={<LiveClass />} />
                    <Route path="/admin/software-downloads" element={<SoftwareDownloads />} />
                    <Route path="/admin/approvals" element={<ApprovalsQueuePage />} />
                </Route>
            </Route>

            {/* Student Routes */}
            <Route
                element={
                    <ProtectedRoute allowedRoles={['student']}>
                        <DashboardLayout />
                    </ProtectedRoute>
                }
            >
                {/* Dashboard & Profile */}
                <Route path="/student" element={<StudentDashboard />} />
                <Route path="/student/profile" element={<StudentProfile />} />

                {/* Legacy routes for compatibility */}

                <Route path="/student/history" element={<TestHistoryPage />} />
                <Route path="/student/result/:sessionId" element={<TestResultPage />} />

                {/* Course Routes */}

                <Route path="/student/course/theory" element={<TheoryCourse />} />

                {/* New Practice Routes */}
                <Route path="/student/keyboard-practice" element={<KeyboardPractice />} />
                <Route path="/student/typing-practice" element={<TypingPractice />} />
                <Route path="/student/mcq-practice" element={<MCQPractice />} />
                <Route path="/student/video-tutorials" element={<VideoTutorials />} />
                <Route path="/student/video/:id" element={<VideoPlayer />} />
                <Route path="/student/typing-tests" element={<Navigate to="/student/typing-test/all" replace />} />

                {/* Typing Test Routes */}
                <Route path="/student/typing-test/session/:testId" element={<TypingTestSessionPage />} />
                <Route path="/student/typing-test/:type" element={<TypingTestPage />} />

                {/* Theory Test Routes */}
                <Route path="/student/theory-test/:category" element={<TheoryTestPage />} />

                {/* Demo Exam */}
                <Route path="/student/demo-exam" element={<DemoExam />} />

                {/* Results */}
                <Route path="/student/results" element={<StudentResults />} />
            </Route>

            {/* Test Session Route */}
            <Route
                element={
                    <ProtectedRoute allowedRoles={['student']}>
                        <TestLayout />
                    </ProtectedRoute>
                }
            >
                <Route path="/test/:sessionId" element={<TestSessionPage />} />
            </Route>

            {/* Root Redirect */}
            <Route
                path="/"
                element={
                    isAuthenticated ? (
                        user?.role === 'student' ? (
                            <Navigate to={labSession ? '/student' : '/login?exam_client_only=1'} replace />
                        ) : user?.role === 'principal' ? (
                            <Navigate to="/admin/principal-dashboard" replace />
                        ) : user?.role === 'senior_instructor' ? (
                            <Navigate to="/admin/senior-instructor-dashboard" replace />
                        ) : user?.role === 'instructor' ? (
                            <Navigate to="/admin/instructor-dashboard" replace />
                        ) : user?.role === 'admin_staff' ? (
                            <Navigate to="/admin/front-office" replace />
                        ) : user?.role === 'accountant' ? (
                            <Navigate to="/admin/accountant-dashboard" replace />
                        ) : user?.role === 'counselor' ? (
                            <Navigate to="/admin/counselor-dashboard" replace />
                        ) : user?.role === 'hr' ? (
                            <Navigate to="/admin/hr-dashboard" replace />
                        ) : user?.role === 'receptionist' ? (
                            <Navigate to="/admin/receptionist" replace />
                        ) : institutePortalRoles.includes(user?.role as typeof institutePortalRoles[number]) ? (
                            <Navigate to="/admin/institute-dashboard" replace />
                        ) : (
                            <Navigate to="/admin" replace />
                        )
                    ) : (
                        <Navigate to="/login" replace />
                    )
                }
            />

            <Route path="/student/dashboard" element={<Navigate to="/student" replace />} />
            <Route path="/student/tests" element={<Navigate to="/student/typing-test/all" replace />} />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
        </ServerLicenseGate>
    );
}

